import { QrcodeIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { BoltIcon, PenPaperIcon } from '@/icons';
import { type Folder } from '@/shared/features/quiz-folder';
import { Button } from '@/ui_v3';
import { useFolderCta } from '../hooks/useFolderCta';

/** zus `Button` 기본값은 아이콘+라벨을 가운데로 모은다. CTA 는 아이콘 바로 뒤부터 좌측 정렬한다. */
const CTA_BUTTON_CLASS = 'w-full justify-start gap-xs px-md';
const SectionLabel = ({
  children
}: {
  children: string;
}) => <p className={'text-caption-lg-semibold text-alternative'} data-sentry-component="SectionLabel" data-sentry-source-file="FolderCtaPanel.tsx">{children}</p>;

/**
 * 우측 CTA 패널. 소유자에게만 그린다(`FolderDetailPageView` 가 게이트한다).
 *
 * 잠긴 퀴즈가 있으면 재생 버튼 자리를 `플랜 결제` 가 대신한다(둘 다 두지 않는다).
 * 빈 폴더는 재생 버튼 없이 `폴더 관리` 만 남는다. `입장 공유` 는 퀴즈 상세처럼 관리 묶음 안에 둔다.
 */
export const FolderCtaPanel = ({
  folder
}: {
  folder: Folder;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    showPayPlan,
    showPlayButton,
    playIcon,
    notice,
    isPlayDisabled,
    startPlay,
    showShareButton,
    shareEntry,
    openPaywall,
    goEdit
  } = useFolderCta(folder);
  return <div className={'flex w-full flex-col gap-xl pb-xl pt-lg'} data-sentry-component="FolderCtaPanel" data-sentry-source-file="FolderCtaPanel.tsx">
      <div className={'flex w-full flex-col gap-xs'}>
        {showPayPlan && <Button variant={'contained'} intent={'gradient'} size={'md'} className={CTA_BUTTON_CLASS} onClick={openPaywall} leftSlot={<BoltIcon size={'2xs'} />}>
            {t('common:detail.payPlan')}
          </Button>}

        {showPlayButton && <Button variant={'contained'} intent={'gradient'} size={'md'} className={CTA_BUTTON_CLASS} disabled={isPlayDisabled} onClick={startPlay} leftSlot={playIcon}>
            {t('common:folder.detail.play.enter')}
          </Button>}

        <div className={'flex w-full flex-col gap-xs pt-sm'}>
          <SectionLabel data-sentry-element="SectionLabel" data-sentry-source-file="FolderCtaPanel.tsx">{t('common:folder.detail.manage.label')}</SectionLabel>
          {/* 퀴즈 상세가 `퀴즈 관리` 아래 두는 `입장 공유` 와 같은 자리. 링크를 받은 사람은 폴더 순서대로 이어 푼다. */}
          {showShareButton && <Button variant={'outlined'} intent={'neutral'} size={'md'} className={CTA_BUTTON_CLASS} onClick={shareEntry} leftSlot={<QrcodeIcon size={'2xs'} />}>
              {t('common:detail.cta.share')}
            </Button>}
          <Button variant={'outlined'} intent={'neutral'} size={'md'} className={CTA_BUTTON_CLASS} onClick={goEdit} leftSlot={<PenPaperIcon size={'2xs'} />} data-sentry-element="Button" data-sentry-source-file="FolderCtaPanel.tsx">
            {t('common:global.edit.button')}
          </Button>
        </div>

        {notice && <p className={'pt-xs text-caption-lg-semibold text-assistive'}>
            {notice}
          </p>}
      </div>
    </div>;
};